import { BadRequestException, Injectable, NotFoundException, Optional } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import {
  EmailLogListResponse,
  EmailLogQuery,
  SendTestEmailRequest,
  TemplateListResponse,
  TemplateStatus,
  TemplateSummary,
  SystemTemplateVersion,
  TemplateVersionListResponse,
  UpsertTemplateRequest,
} from '@cart/shared-types';
import { EmailService } from '../notifications/email.service';
import { TemplateRenderService } from '../notifications/template-render.service';
import { StoreContextService } from '../tenancy/store-context.service';

// Fixed placeholder values for Send Test Email — a test send exercises the
// real render pipeline without needing a real order/user/cart behind it.
// Deliberately does NOT include logoUrl/primaryColor: those come from the
// real EmailSettings row via TemplateRenderService's branding defaults, so a
// test send shows actual configured branding, not a fake sample color.
const SAMPLE_VARIABLES: Record<string, string> = {
  customerName: 'Test User',
  orderNumber: 'TEST-0001',
  grandTotal: '₹999.00',
  itemsList: 'Sample Product × 1',
  carrier: 'Sample Carrier',
  trackingNumber: 'TRACK123456',
  reason: 'Sample cancellation reason',
  refundAmount: '₹499.00',
  resetUrl: 'https://example.com/reset-password?token=sample',
  cartUrl: 'https://example.com/cart',
};

type TemplateTypeRow = {
  id: string;
  code: string;
  name: string;
  category: string;
  description: string | null;
  isActive: boolean;
  defaultName: string | null;
  defaultSubject: string | null;
  defaultBodyHtml: string | null;
  /** When the platform last revised the default — shown as its release date. */
  updatedAt: Date;
};

type TemplateRow = {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  isActive: boolean;
  isPublished: boolean;
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
};

@Injectable()
export class TemplatesService {
  constructor(
    private readonly prisma: PrismaService,
    @Optional() private readonly storeContext?: StoreContextService,
    @Optional() private readonly emailService?: EmailService,
    @Optional() private readonly templateRender?: TemplateRenderService
  ) {}

  async list(): Promise<TemplateListResponse> {
    const types = await this.prisma.templateType.findMany({
      where: { isActive: true },
      include: {
        templates: { where: { storeId: this.storeId() } },
        _count: { select: { templates: true } },
      },
      orderBy: [{ category: 'asc' }, { sortOrder: 'asc' }, { name: 'asc' }],
    });

    const items = await Promise.all(types.map((type) => this.toSummary(type)));

    return { items, total: items.length };
  }

  async getByCode(typeCode: string): Promise<TemplateSummary> {
    const type = await this.findType(typeCode);
    return this.toSummary(type);
  }

  async save(
    typeCode: string,
    input: UpsertTemplateRequest,
    actorId: string
  ): Promise<TemplateSummary> {
    const type = await this.findType(typeCode);
    const storeId = this.storeId();
    const existing = type.templates[0];
    const wasPublished = existing?.isPublished ?? false;

    const template = await this.prisma.template.upsert({
      where: { storeId_templateTypeId: { storeId, templateTypeId: type.id } },
      create: {
        storeId,
        templateTypeId: type.id,
        name: input.name,
        subject: input.subject,
        bodyHtml: input.bodyHtml,
        isActive: input.isActive ?? true,
        isPublished: input.publish,
        createdBy: actorId,
        updatedBy: actorId,
      },
      update: {
        name: input.name,
        subject: input.subject,
        bodyHtml: input.bodyHtml,
        isActive: input.isActive ?? true,
        isPublished: input.publish,
        updatedBy: actorId,
      },
    });

    const action = input.publish ? (wasPublished ? 'edited' : 'published') : 'edited';
    await this.createVersion(template, actorId, action);

    return this.getByCode(typeCode);
  }

  async setStatus(typeCode: string, isActive: boolean, actorId: string): Promise<TemplateSummary> {
    const type = await this.findType(typeCode);
    const existing = type.templates[0];

    if (!existing) {
      // Copy-on-write.
      //
      // Since the system default moved onto the type, "no row" is the normal
      // state for most stores — so refusing here 404'd the enable/disable
      // toggle on every template a store had not customised, which is nearly
      // all of them. Turning one off IS a per-store decision, so it
      // materialises an override seeded from the default and carrying the
      // requested state.
      if (!type.defaultSubject || !type.defaultBodyHtml) {
        throw new NotFoundException(`Template "${typeCode}" has not been configured yet`);
      }

      const created = await this.prisma.template.create({
        data: {
          storeId: this.storeId(),
          templateTypeId: type.id,
          name: type.defaultName ?? type.name,
          subject: type.defaultSubject,
          bodyHtml: type.defaultBodyHtml,
          isActive,
          isPublished: true,
          createdBy: actorId,
          updatedBy: actorId,
        },
      });

      await this.createVersion(created, actorId, isActive ? 'enabled' : 'disabled');
      return this.getByCode(typeCode);
    }

    const template = await this.prisma.template.update({
      where: { id: existing.id },
      data: { isActive, updatedBy: actorId },
    });

    await this.createVersion(template, actorId, isActive ? 'enabled' : 'disabled');

    return this.getByCode(typeCode);
  }

  async listVersions(typeCode: string): Promise<TemplateVersionListResponse> {
    const type = await this.findType(typeCode);
    const existing = type.templates[0];

    // The platform's own version travels with the history, always — including
    // for a store with no copy at all, where it IS what the store sends.
    // A merchant cannot adopt an improvement they were never shown.
    const systemDefault = this.systemVersion(type, existing);

    if (!existing) {
      return { items: [], total: 0, systemDefault };
    }

    const versions = await this.prisma.templateVersion.findMany({
      where: { templateId: existing.id },
      orderBy: { versionNumber: 'desc' },
    });

    return {
      systemDefault,
      items: versions.map((v) => ({
        id: v.id,
        versionNumber: v.versionNumber,
        name: v.name,
        subject: v.subject,
        bodyHtml: v.bodyHtml,
        isActive: v.isActive,
        isPublished: v.isPublished,
        action: v.action,
        comment: v.comment ?? undefined,
        createdBy: v.createdBy,
        createdAt: v.createdAt.toISOString(),
      })),
      total: versions.length,
    };
  }

  /**
   * The platform's current default for a type, and whether this store is on it.
   *
   * `matchesCurrent` compares the rendered content rather than a version
   * number, because the two live in different tables and there is no shared
   * counter to compare — and because content is what a merchant actually cares
   * about. Whitespace is trimmed so a trailing newline does not report a store
   * as out of date.
   */
  private systemVersion(
    type: { defaultName: string | null; defaultSubject: string | null; defaultBodyHtml: string | null; updatedAt: Date; name: string },
    existing?: { subject: string; bodyHtml: string }
  ): SystemTemplateVersion | undefined {
    if (!type.defaultSubject || !type.defaultBodyHtml) return undefined;

    const same =
      !!existing &&
      existing.subject.trim() === type.defaultSubject.trim() &&
      existing.bodyHtml.trim() === type.defaultBodyHtml.trim();

    return {
      name: type.defaultName ?? type.name,
      subject: type.defaultSubject,
      bodyHtml: type.defaultBodyHtml,
      updatedAt: type.updatedAt.toISOString(),
      // With no override the store renders the default itself, so it is by
      // definition current.
      matchesCurrent: existing ? same : true,
      hasOverride: !!existing,
    };
  }

  async rollback(typeCode: string, versionId: string, actorId: string): Promise<TemplateSummary> {
    const type = await this.findType(typeCode);
    const existing = type.templates[0];

    if (!existing) {
      throw new NotFoundException(`Template "${typeCode}" has not been configured yet`);
    }

    const version = await this.prisma.templateVersion.findFirst({
      where: { id: versionId, templateId: existing.id },
    });

    if (!version) {
      throw new NotFoundException('Version not found');
    }

    const template = await this.prisma.template.update({
      where: { id: existing.id },
      data: {
        name: version.name,
        subject: version.subject,
        bodyHtml: version.bodyHtml,
        updatedBy: actorId,
      },
    });

    await this.createVersion(template, actorId, 'rollback', `Rolled back to version ${version.versionNumber}`);

    return this.getByCode(typeCode);
  }

  /**
   * Reverts this store to the platform default by DELETING its override.
   *
   * It used to copy version 1 back over the row, which needed a version-1 row
   * to exist and left a per-store copy behind. Now the default lives on the
   * type, so "restore" is simply removing the override and letting resolution
   * fall through — which is also what keeps the table from refilling with
   * copies of the default.
   */
  async restoreDefault(typeCode: string, actorId: string): Promise<TemplateSummary> {
    const type = await this.findType(typeCode);
    const existing = type.templates[0];

    if (!type.defaultSubject || !type.defaultBodyHtml) {
      throw new NotFoundException(`Template "${typeCode}" has no system default to restore`);
    }

    if (existing) {
      // Versions cascade with the template row; the history of an override the
      // merchant has explicitly discarded is not worth keeping.
      await this.prisma.template.delete({ where: { id: existing.id } });
    }

    return this.getByCode(typeCode);
  }

  async sendTest(typeCode: string, input: SendTestEmailRequest): Promise<void> {
    if (!input.email?.trim()) {
      throw new BadRequestException('email is required');
    }

    const type = await this.findType(typeCode);
    const existing = type.templates[0];
    const subject = input.subject ?? existing?.subject;
    const bodyHtml = input.bodyHtml ?? existing?.bodyHtml;

    if (!subject || !bodyHtml) {
      throw new BadRequestException('Nothing to send — save the template first or provide content');
    }

    const rendered = await this.templateRender?.render(typeCode, SAMPLE_VARIABLES, { subject, bodyHtml });

    if (!rendered) {
      throw new BadRequestException('Email sending is not configured');
    }

    await this.emailService?.send({
      to: input.email.trim(),
      subject: `[TEST] ${rendered.subject}`,
      html: rendered.html,
      typeCode, // attributes Send Test entries in the Email Logs page
    });
  }

  async listEmailLogs(query: EmailLogQuery): Promise<EmailLogListResponse> {
    const limit = Math.min(query.limit ?? 100, 500);
    const logs = await this.prisma.emailLog.findMany({
      where: {
        storeId: this.storeId(),
        ...(query.status ? { status: query.status } : {}),
        ...(query.typeCode ? { typeCode: query.typeCode } : {}),
      },
      orderBy: { createdAt: 'desc' },
      take: limit,
    });

    const typeCodes = [...new Set(logs.map((l) => l.typeCode).filter((c): c is string => !!c))];
    const types = typeCodes.length
      ? await this.prisma.templateType.findMany({ where: { code: { in: typeCodes } }, select: { code: true, name: true } })
      : [];
    const nameByCode = new Map(types.map((t) => [t.code, t.name]));

    return {
      items: logs.map((log) => ({
        id: log.id,
        typeCode: log.typeCode ?? undefined,
        typeName: log.typeCode ? nameByCode.get(log.typeCode) : undefined,
        recipient: log.recipient,
        subject: log.subject,
        status: log.status as 'sent' | 'failed',
        errorMessage: log.errorMessage ?? undefined,
        createdAt: log.createdAt.toISOString(),
      })),
      total: logs.length,
    };
  }

  private async findType(typeCode: string): Promise<TemplateTypeRow & { templates: TemplateRow[] }> {
    const type = await this.prisma.templateType.findFirst({
      where: { code: typeCode, isActive: true },
      include: { templates: { where: { storeId: this.storeId() } } },
    });

    if (!type) {
      throw new NotFoundException(`Template type "${typeCode}" not found`);
    }

    return type;
  }

  private async createVersion(
    template: TemplateRow,
    actorId: string,
    action: string,
    comment?: string
  ): Promise<void> {
    const last = await this.prisma.templateVersion.findFirst({
      where: { templateId: template.id },
      orderBy: { versionNumber: 'desc' },
      select: { versionNumber: true },
    });

    await this.prisma.templateVersion.create({
      data: {
        templateId: template.id,
        versionNumber: (last?.versionNumber ?? 0) + 1,
        name: template.name,
        subject: template.subject,
        bodyHtml: template.bodyHtml,
        isActive: template.isActive,
        isPublished: template.isPublished,
        action,
        comment,
        createdBy: actorId,
      },
    });
  }

  /**
   * Resolves what this store actually sends: its override if it has one, the
   * type's system default otherwise.
   *
   * A store with no row is NOT unconfigured — it is on the platform default and
   * its emails send normally. That distinction is why `system_default` exists
   * as a status separate from `not_configured`, which now means the type has no
   * default either and nothing would send.
   */
  private async toSummary(type: TemplateTypeRow & { templates: TemplateRow[] }): Promise<TemplateSummary> {
    const template = type.templates[0];
    const hasDefault = !!(type.defaultSubject && type.defaultBodyHtml);
    const status = this.deriveStatus(template, hasDefault);
    const versionCount = template
      ? await this.prisma.templateVersion.count({ where: { templateId: template.id } })
      : 0;

    return {
      typeId: type.id,
      typeCode: type.code,
      typeName: type.name,
      category: type.category,
      description: type.description ?? undefined,
      typeIsActive: type.isActive,
      templateId: template?.id,
      // Falls through to the default so Preview and Edit always open with the
      // content that would really be sent, rather than a blank pane.
      name: template?.name ?? type.defaultName ?? undefined,
      subject: template?.subject ?? type.defaultSubject ?? undefined,
      bodyHtml: template?.bodyHtml ?? type.defaultBodyHtml ?? undefined,
      isActive: template?.isActive ?? hasDefault,
      isPublished: template?.isPublished ?? hasDefault,
      status,
      versionCount,
      createdAt: template?.createdAt.toISOString(),
      updatedAt: template?.updatedAt.toISOString(),
      updatedBy: template?.updatedBy,
    };
  }

  private deriveStatus(template: TemplateRow | undefined, hasDefault = false): TemplateStatus {
    // No override: on the system default if there is one, genuinely
    // unconfigured only when there isn't.
    if (!template) return hasDefault ? 'system_default' : 'not_configured';
    if (!template.isPublished) return 'draft';
    return template.isActive ? 'active' : 'disabled';
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}
